"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { cookieSet } from "@/features/platform/platform.cookie";
import type { Theme } from "@/features/theme/theme.constants";
import { THEME_COOKIE_NAME } from "@/features/theme/theme.constants";

function getSystemResolvedTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function subscribeToSystemTheme(callback: () => void) {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  media.addEventListener("change", callback);
  return () => media.removeEventListener("change", callback);
}

function applyTheme(resolved: "light" | "dark") {
  const root = document.documentElement;
  // /scan/do locks clinic-light chrome; do not re-apply html.dark while that page is open.
  if (root.hasAttribute("data-do-phone-light")) {
    root.classList.remove("dark");
    return;
  }
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  initialTheme,
  children,
}: {
  initialTheme: Theme;
  children: React.ReactNode;
}) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);
  const systemResolved = useSyncExternalStore(
    subscribeToSystemTheme,
    getSystemResolvedTheme,
    () => "light" as const
  );
  const resolvedTheme =
    theme === "light" ? "light" : theme === "dark" ? "dark" : systemResolved;

  const setTheme = useCallback(async (newTheme: Theme) => {
    await cookieSet(THEME_COOKIE_NAME, newTheme, {
      path: "/",
      maxAge: 365 * 24 * 60 * 60,
    });
    setThemeState(newTheme);
    const resolved = newTheme === "light" ? "light" : newTheme === "dark" ? "dark" : getSystemResolvedTheme();
    applyTheme(resolved);
  }, []);

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
